import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { AxiosHeaders } from 'axios';
import { getApiBaseUrl } from '../utils/env';
import { hasSessionFlag, clearSession } from '../utils/authGuard';
import { ensureCsrfToken, CSRF_HEADER } from '../utils/csrf';

// ─── Refresh queue management ───────────────────────────────────────────────

let isRefreshing = false;
let failedQueue: Array<{
  resolve: () => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
}

function clearAuthData(): void {
  clearSession();
  try {
    const stored = localStorage.getItem('admin-auth-storage');
    if (stored) {
      const data = JSON.parse(stored) as {
        state?: { user: null; isAuthenticated: boolean };
      };
      if (data.state) {
        data.state.user = null;
        data.state.isAuthenticated = false;
        localStorage.setItem('admin-auth-storage', JSON.stringify(data));
      }
    }
  } catch {
    // ignore
  }
}

let isLoggingOut = false;
function forceLogout(): void {
  if (isLoggingOut) return;
  isLoggingOut = true;
  clearAuthData();
  window.dispatchEvent(new CustomEvent('app:unauthorized'));
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// ─── Request interceptors ───────────────────────────────────────────────────

// Both auth tokens are httpOnly cookies the browser attaches automatically —
// there's no token for this interceptor to read or attach. Its only job now
// is echoing the (JS-readable) CSRF cookie back as a header on requests that
// mutate state, per the double-submit pattern the backend expects.
export function createAuthRequestInterceptor(): (
  config: InternalAxiosRequestConfig
) => Promise<InternalAxiosRequestConfig> {
  return async function (
    config: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> {
    if (typeof window === 'undefined') return config;

    const method = (config.method || 'get').toLowerCase();
    if (MUTATING_METHODS.has(method)) {
      const csrfToken = await ensureCsrfToken();
      if (csrfToken) {
        const headers = AxiosHeaders.from(config.headers);
        headers.set(CSRF_HEADER, csrfToken);
        config.headers = headers;
      }
    }

    return config;
  };
}

export function createRequestErrorInterceptor(): (
  error: unknown
) => Promise<never> {
  return function (error: unknown): Promise<never> {
    return Promise.reject(
      error instanceof Error ? error : new Error('Request failed')
    );
  };
}

// ─── Response interceptors ──────────────────────────────────────────────────

export function createResponseSuccessInterceptor(): <T = unknown>(
  response: AxiosResponse<T>
) => AxiosResponse<T> {
  return function <T = unknown>(response: AxiosResponse<T>): AxiosResponse<T> {
    return response;
  };
}

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export function createResponseErrorInterceptor(
  axiosInstance: AxiosInstance
): (error: AxiosError) => Promise<unknown> {
  return async function (error: AxiosError): Promise<unknown> {
    if (error.response?.status !== 401) {
      if (error.response) {
        console.error({ status: error.response.status, data: error.response.data });
      } else if (error.request) {
        console.error(error.request);
      } else {
        console.error('Error', error.message);
      }
      return Promise.reject(error);
    }

    if (typeof window === 'undefined') return Promise.reject(error);

    const originalRequest = error.config as RetryableConfig | undefined;
    if (!originalRequest) return Promise.reject(error);

    // Do not attempt refresh on the refresh endpoint itself —
    // prevents infinite loops when the refresh token is invalid.
    if (originalRequest.url?.includes('/auth/refresh')) {
      forceLogout();
      return Promise.reject(error);
    }

    // Do not retry a request that has already been retried once.
    // This prevents burning a rotated refresh token on a second attempt.
    if (originalRequest._retry) {
      forceLogout();
      return Promise.reject(error);
    }

    if (!hasSessionFlag()) {
      forceLogout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<unknown>((resolve, reject) => {
        failedQueue.push({
          resolve: () => {
            originalRequest._retry = true;
            resolve(axiosInstance(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      // Use fetch to avoid circular dependency with axiosClient
      const apiBase = getApiBaseUrl('/api');
      const csrfToken = await ensureCsrfToken();
      const res = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: csrfToken ? { [CSRF_HEADER]: csrfToken } : undefined,
      });

      if (!res.ok) throw new Error('Token refresh failed');

      // The new accessToken cookie is already set by the response above —
      // just retry; the browser attaches it automatically.
      processQueue(null);
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  };
}
